import { useState, useEffect, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import "./css/Layout.css";
import "./css/Master.css";
import "./css/MasterHub.css";
import api from "./api/api";
import logo from "./Images/logo.png";
import {
  FiMenu, FiX, FiArrowLeft, FiChevronDown, FiSearch,
  FiClock, FiArrowRight,
  FiHome, FiTruck, FiUsers, FiTag, FiLayers, FiBox, FiSliders,
  FiDroplet, FiGrid, FiPackage, FiShoppingCart, FiTrendingUp,
  FiCreditCard, FiFileText, FiLogOut,
} from "react-icons/fi";

/* ── Group definitions ──────────────────────────────────────────────────── */
const GROUPS = [
  { key: "core",         label: "Core masters",  match: ["company","supplier","customer"] },
  { key: "product",      label: "Product setup",  match: ["category","subcategory","product","variant","size","colour","color","stock"] },
  { key: "transactions", label: "Transactions",   match: ["purchase","sales","payment"] },
];

function getGroupKey(name = "") {
  const n = name.toLowerCase().replace(/\s+/g,"");
  for (const g of GROUPS) {
    if (g.match.some(k => n.includes(k))) return g.key;
  }
  return "other";
}

const GROUP_ORDER = ["core","product","transactions","other"];
const GROUP_LABELS = { core:"Core masters", product:"Product setup", transactions:"Transactions", other:"Other" };

const ICON_RULES = [
  { match:["subcategory"],     icon: FiLayers },
  { match:["category"],        icon: FiTag },
  { match:["company"],         icon: FiHome },
  { match:["supplier"],        icon: FiTruck },
  { match:["customer"],        icon: FiUsers },
  { match:["variant"],         icon: FiGrid },
  { match:["size"],            icon: FiSliders },
  { match:["colour","color"],  icon: FiDroplet },
  { match:["stock"],           icon: FiPackage },
  { match:["purchasedetails"], icon: FiFileText },
  { match:["purchase"],        icon: FiShoppingCart },
  { match:["salesdetails"],    icon: FiFileText },
  { match:["sales"],           icon: FiTrendingUp },
  { match:["payment"],         icon: FiCreditCard },
  { match:["product"],         icon: FiBox },
];

function getIcon(name = "") {
  const n = name.toLowerCase().replace(/\s+/g,"");
  for (const rule of ICON_RULES) {
    if (rule.match.some(k => n.includes(k))) return rule.icon;
  }
  return FiFileText;
}

function buildGroups(submenus, query) {
  const q = query.toLowerCase();
  const filtered = submenus.filter(s => s.submenu_name.toLowerCase().includes(q));
  return GROUP_ORDER
    .map(key => ({ key, label: GROUP_LABELS[key], items: filtered.filter(s => getGroupKey(s.submenu_name) === key) }))
    .filter(g => g.items.length > 0);
}

/* ── Recently-used (localStorage, no API) ───────────────────────────────── */
const RECENT_KEY = "ys_recent_masters";
function getRecent() {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; } catch { return []; }
}
function pushRecent(filename, name) {
  const list = getRecent().filter(r => r.filename !== filename);
  list.unshift({ filename, name });
  localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 5)));
}

/* ── Greeting ────────────────────────────────────────────────────────────── */
function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

function getUser() {
  try { return JSON.parse(localStorage.getItem("user")) || {}; } catch { return {}; }
}

/* ── MasterHub component ─────────────────────────────────────────────────── */
function MasterHub({ submenus, onSelect }) {
  const [query, setQuery]       = useState("");
  const [recent, setRecent]     = useState(getRecent);
  const groups                  = buildGroups(submenus, query);
  const user                    = getUser();
  const userName                = user.loginname || user.name || "Admin";

  // Ctrl+K focuses search
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        document.getElementById("hub-search-input")?.focus();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const handleSelect = (filename, name) => {
    pushRecent(filename, name);
    setRecent(getRecent());
    onSelect(filename);
  };

  return (
    <div className="master-hub">

      {/* 1. Banner header */}
      <div className="hub-banner">
        <div className="hub-banner-left">
          <img src={logo} alt="YashShop logo" className="hub-banner-logo" />
          <div>
            <p className="hub-banner-title">YashShop ERP</p>
            <p className="hub-banner-greeting">{greeting()}, {userName} 👋</p>
          </div>
        </div>
        <div className="hub-banner-stats">
          <div className="hub-banner-stat">
            <span className="hub-banner-stat-val">{submenus.length}</span>
            <span className="hub-banner-stat-label">Total masters</span>
          </div>
          <div className="hub-banner-stat">
            <span className="hub-banner-stat-val">{GROUPS.length}</span>
            <span className="hub-banner-stat-label">Groups</span>
          </div>
        </div>
      </div>

      {/* 5. Recently used */}
      {recent.length > 0 && (
        <div className="hub-recent">
          <div className="hub-recent-label"><FiClock size={12} /> Recently used</div>
          <div className="hub-recent-list">
            {recent.map(r => (
              <div key={r.filename} className="hub-recent-chip" onClick={() => handleSelect(r.filename, r.name)}>
                <FiClock size={12} /> {r.name}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 2. Search with icon + Ctrl K */}
      <div className="hub-search-wrap">
        <span className="hub-search-icon"><FiSearch /></span>
        <input
          id="hub-search-input"
          className="hub-search"
          type="text"
          placeholder="Search masters..."
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
        <span className="hub-search-kbd">Ctrl K</span>
      </div>

      {groups.length === 0 && <p className="hub-empty">No matching masters found.</p>}

      {/* 3+4. Group labels + colored cards */}
      {groups.map(g => (
        <div className="hub-group" key={g.key}>
          <div className="hub-group-header">
            <div className={`hub-group-bar ${g.key}`} />
            <span className="hub-group-name">{g.label}</span>
            <span className={`hub-group-count ${g.key}`}>{g.items.length} items</span>
          </div>

          <div className="hub-grid">
            {g.items.map(s => {
              const Icon = getIcon(s.submenu_name);
              return (
                <div key={s.filename} className="hub-card" onClick={() => handleSelect(s.filename, s.submenu_name)}>
                  <div className={`hub-card-icon ${g.key}`}><Icon /></div>
                  <div className="hub-card-bottom">
                    <span className="hub-card-label">{s.submenu_name}</span>
                    <FiArrowRight className="hub-card-arrow" />
                  </div>
                  <span className={`hub-card-badge ${g.key}`}>{g.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── MasterDetailHeader ──────────────────────────────────────────────────── */
function MasterDetailHeader({ submenus, activeFile, onBack, onSelect }) {
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState("");
  const activeName        = submenus.find(s => s.filename === activeFile)?.submenu_name || "";
  const groups            = buildGroups(submenus, query);

  return (
    <div className="master-detail-header">
      <button className="master-back-btn" onClick={onBack}>
        <FiArrowLeft size={14} /> All masters
      </button>

      <h2 className="master-detail-title">{activeName}</h2>

      <div style={{ position:"relative" }}>
        <button className="switcher-btn" onClick={() => setOpen(o => !o)}>
          <span>Switch master</span> <FiChevronDown size={13} />
        </button>

        {open && (
          <>
            <div className="switcher-backdrop" onClick={() => setOpen(false)} />
            <div className="switcher-panel">
              <input
                className="switcher-search"
                type="text"
                placeholder="Search master..."
                autoFocus
                value={query}
                onChange={e => setQuery(e.target.value)}
              />
              {groups.length === 0 && <p className="hub-empty">No match.</p>}
              {groups.map(g => (
                <div key={g.key}>
                  <div className="switcher-group-label">{g.label}</div>
                  {g.items.map(s => {
                    const Icon = getIcon(s.submenu_name);
                    return (
                      <div
                        key={s.filename}
                        className={s.filename === activeFile ? "switcher-item active" : "switcher-item"}
                        onClick={() => { onSelect(s.filename); pushRecent(s.filename, s.submenu_name); setOpen(false); setQuery(""); }}
                      >
                        <Icon size={14} /> {s.submenu_name}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ── Layout root ─────────────────────────────────────────────────────────── */
function Layout() {
  const [menuData, setMenuData]             = useState([]);
  const [menu, setMenu]                     = useState("");
  const [submenu, setSubmenu]               = useState("");
  const [Component, setComponent]           = useState(null);
  const [navOpen, setNavOpen]               = useState(true);
  const [autoLoaded, setAutoLoaded]         = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const res = await api.get("/Menu/getmenu");
        const menuMap = {};
        res.data.forEach(item => {
          if (!menuMap[item.menu_key]) {
            menuMap[item.menu_key] = { menu_name: item.menu_name, menu_key: item.menu_key, submenus: [] };
          }
          if (item.submenu_name && item.filename) {
            menuMap[item.menu_key].submenus.push({ submenu_name: item.submenu_name, filename: item.filename });
          }
        });
        setMenuData(Object.values(menuMap));
      } catch (err) {
        console.error("Menu API Error:", err);
      }
    };
    fetchMenu();
  }, []);

  const handleLogout = () => { localStorage.clear(); navigate("/"); };

  const loadComponent = (file) => {
    if (!file) return;
    const DynamicComponent = lazy(() => import(`./${file}.js`));
    setSubmenu(file);
    setComponent(() => DynamicComponent);
  };

  // Auto-load Dashboard on first login
  useEffect(() => {
    if (autoLoaded || menuData.length === 0) return;
    const dash = menuData.find(m =>
      m.menu_key?.toLowerCase() === "dashboard" || m.menu_name?.toLowerCase() === "dashboard"
    );
    if (dash) {
      setMenu(dash.menu_key);
      setNavOpen(true);
      loadComponent(dash.submenus?.[0]?.filename || "Dashboard");
    }
    setAutoLoaded(true);
  }, [menuData, autoLoaded]);

  const currentMenuEntry = menuData.find(m => m.menu_key === menu);
  const activeMenuName   = currentMenuEntry?.menu_name || "";
  const isMasterMenu     = currentMenuEntry?.menu_name?.toLowerCase() === "master"
                         || currentMenuEntry?.menu_key?.toLowerCase()  === "master";

  return (
    <div className="layout-container">

      {mobileSidebarOpen && (
        <div className="sidebar-backdrop" onClick={() => setMobileSidebarOpen(false)} />
      )}

      {/* ── Sidebar ── */}
      <div className={`sidebar${mobileSidebarOpen ? " open" : ""}`}>

        {/* Logo slot */}
        <div className="sidebar-logo">
          <img src={logo} alt="YashShop" className="sidebar-logo-img" style={{ width:36, height:36, objectFit:"contain", borderRadius:8, background:"rgba(255,255,255,0.12)", padding:2, flexShrink:0 }} />
          <div className="sidebar-logo-text">
            <span className="sidebar-logo-name">YashShop</span>
            <span className="sidebar-logo-tagline">ERP System</span>
          </div>
        </div>

        <p className="menu-title">Navigation</p>

        {menuData.map(m => (
          <button
            key={m.menu_key}
            className={menu === m.menu_key ? "active" : ""}
            onClick={() => {
              const isMaster = m.menu_name?.toLowerCase() === "master"
                             || m.menu_key?.toLowerCase()  === "master";
              setMenu(m.menu_key);
              setNavOpen(true);
              setMobileSidebarOpen(false);

              if (isMaster) {
                // Master → show hub grid, reset component
                setSubmenu("");
                setComponent(null);
              } else if (m.submenus?.length > 0) {
                // Any other menu (Dashboard, Reports…) → auto-load first submenu
                loadComponent(m.submenus[0].filename);
              } else {
                setSubmenu("");
                setComponent(null);
              }
            }}
          >
            {m.menu_name}
          </button>
        ))}

        <div className="sidebar-bottom">
          <button className="logout-btn" onClick={handleLogout}>
            <FiLogOut size={14} style={{ marginRight:6 }} /> Logout
          </button>
        </div>
      </div>

      {/* ── Content area ── */}
      <div className="content">

        {/* Mobile topbar */}
        <div className="mobile-topbar">
          <button className="mobile-topbar-btn" onClick={() => setMobileSidebarOpen(o => !o)} aria-label="Toggle menu">
            {mobileSidebarOpen ? <FiX /> : <FiMenu />}
          </button>
          <img src={logo} alt="" className="mobile-topbar-logo" style={{ width:28, height:28, objectFit:"contain", borderRadius:6, background:"rgba(255,255,255,0.15)", padding:2 }} />
          <span className="mobile-topbar-title">{activeMenuName || "YashShop"}</span>
        </div>

        {isMasterMenu ? (
          <div className="page-content">
            {Component ? (
              <>
                <MasterDetailHeader
                  submenus={currentMenuEntry.submenus}
                  activeFile={submenu}
                  onBack={() => { setComponent(null); setSubmenu(""); }}
                  onSelect={loadComponent}
                />
                <Suspense fallback={<div style={{ padding:32, color:"var(--ink-muted)" }}>Loading…</div>}>
                  <Component />
                </Suspense>
              </>
            ) : (
              <MasterHub submenus={currentMenuEntry?.submenus || []} onSelect={loadComponent} />
            )}
          </div>
        ) : (
          <>
            {/* ── Page header bar (replaces bare topnav label) ── */}
            {menu && (
              <div className="page-header-bar">
                <div className="page-header-left">
                  <span className="page-header-crumb">YashShop</span>
                  <span className="page-header-sep">›</span>
                  <span className="page-header-title">{activeMenuName}</span>
                  {submenu && currentMenuEntry?.submenus?.length > 1 && (
                    <>
                      <span className="page-header-sep">›</span>
                      <span className="page-header-active">
                        {currentMenuEntry.submenus.find(s => s.filename === submenu)?.submenu_name || ""}
                      </span>
                    </>
                  )}
                </div>

                {/* Only show tabs when there are multiple submenus */}
                {currentMenuEntry?.submenus?.length > 1 && (
                  <div className="page-header-tabs">
                    {currentMenuEntry.submenus.map((s, i) => (
                      <button
                        key={i}
                        className={submenu === s.filename ? "ph-tab active" : "ph-tab"}
                        onClick={() => loadComponent(s.filename)}
                      >
                        {s.submenu_name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="page-content">
              {Component && (
                <Suspense fallback={<div style={{ padding:32, color:"var(--ink-muted)" }}>Loading…</div>}>
                  <Component />
                </Suspense>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Layout;